import type {
  CatalogEvent,
  CatalogMatchDetail,
  MatchEvent,
  ProjectState,
  VarDecision,
  VarSubject,
} from "@livcg/schema";
import { formatEventMinute } from "@livcg/schema";
import { useState } from "react";
import { api } from "../shared/api.js";
import { sendRaw } from "../shared/sendRaw.js";
import { Button, type ButtonProps } from "../ui/index.js";
import { eventLabel } from "./eventBadges.js";
import { useMatchDetail } from "./useMatchDetail.js";
import { correctionTarget, type VarCorrection, varPickTypes, varSubjectEvents } from "./varEvents.js";

// SKORE var_json blocks — button texts verbatim.
const SUBJECTS: { id: VarSubject; label: string }[] = [
  { id: "goal", label: "Gol" },
  { id: "penalty", label: "Penalti" },
  { id: "red_card", label: "Qizil karta" },
  { id: "ident", label: "Ident xatosi" },
];

const DECISIONS: Partial<
  Record<
    VarSubject,
    { d: VarDecision; label: string; variant: NonNullable<ButtonProps["variant"]> }[]
  >
> = {
  goal: [
    { d: "yes", label: "Gol", variant: "success" },
    { d: "no", label: "Gol yo‘q", variant: "secondary" },
  ],
  penalty: [
    { d: "yes", label: "Penalti", variant: "success" },
    { d: "no", label: "Penalti yo‘q", variant: "secondary" },
  ],
  red_card: [
    { d: "yes", label: "Qizil karta", variant: "live" },
    { d: "no", label: "Qizil yo‘q", variant: "secondary" },
    { d: "yellow", label: "Sariq karta", variant: "warning" },
    { d: "yellow_no", label: "Sariq yo‘q", variant: "secondary" },
    { d: "yellow_cancel", label: "Sariq bekor qilindi", variant: "secondary" },
  ],
  ident: [{ d: "yes", label: "Ident xatosi", variant: "success" }],
};

const SELECT = "h-8 w-full rounded-md border border-border bg-surface px-2 text-[12.5px] text-ink";
const NOT_FOUND_MSG = "Mos event topilmadi — EventLog'dan qo'lda tuzating.";

type PendingApply = { kind: VarCorrection };

async function fetchMatchEvents(matchId: string): Promise<MatchEvent[]> {
  const detail = await api.get<CatalogMatchDetail & { events: MatchEvent[] }>(
    `/api/catalog/matches/${matchId}`,
  );
  return detail.events;
}

/** SKORE scorevar control: 4 subjects → decision block → Hide.
 *  When a decision is pressed the graphic itself shows "Tekshiruv yakunlandi" for 3s. */
export function VarControls({ slug, state }: { slug: string; state: ProjectState }) {
  const v = state.varOverlay;
  const md = useMatchDetail(state);
  // Goal / card review: before "Boshlash" the operator picks the logged event
  // under review (penalty / ident start right away).
  const [pendingSubject, setPendingSubject] = useState<VarSubject | null>(null);
  const [subjectEventId, setSubjectEventId] = useState("");
  const [pendingApply, setPendingApply] = useState<PendingApply | null>(null);
  const [applyWarning, setApplyWarning] = useState<string | null>(null);

  const pickable = pendingSubject ? varSubjectEvents(pendingSubject, md.sortedEvents) : [];
  // The newest one is preselected — once the refetched log arrives too.
  const pickedId = subjectEventId || pickable[0]?.id || "";

  const selectSubject = (subject: VarSubject) => {
    if (varPickTypes(subject)) {
      md.refetch();
      setPendingSubject(subject);
      setSubjectEventId("");
      return;
    }
    void sendRaw("var.show", { slug, subject, subjectPlayerId: null, subjectEventId: null });
  };
  const startPending = () => {
    if (!pendingSubject) return;
    const ev = pickable.find((e) => e.id === pickedId);
    void sendRaw("var.show", {
      slug,
      subject: pendingSubject,
      subjectPlayerId: ev?.playerId ?? null,
      subjectEventId: ev?.id ?? null,
    });
    setPendingSubject(null);
  };
  const decide = (decision: VarDecision) => {
    void sendRaw("var.decide", { slug, decision });
    setApplyWarning(null);
    const picked = md.sortedEvents.find((e) => e.id === v.subjectEventId);
    if (v.subject === "red_card" && decision === "yes") {
      if (picked?.type === "yellow_card") setPendingApply({ kind: "upgrade" });
    } else if (v.subject === "red_card" && (decision === "no" || decision === "yellow_no")) {
      setPendingApply({ kind: "rescind" });
    } else if (v.subject === "red_card" && decision === "yellow") {
      setPendingApply({ kind: "downgrade" });
    } else if (v.subject === "red_card" && decision === "yellow_cancel") {
      setPendingApply({ kind: "yellow_cancel" });
    } else if (v.subject === "goal" && decision === "no") {
      setPendingApply({ kind: "goal_cancel" });
    }
  };
  const hide = () => {
    void sendRaw("var.hide", { slug });
    setPendingSubject(null);
    setPendingApply(null);
    setApplyWarning(null);
  };

  const applyCorrection = async () => {
    if (!pendingApply) return;
    if (!v.subjectEventId && !v.subjectPlayerId) {
      setApplyWarning("Voqea tanlanmagan — EventLog'dan qo'lda tuzating.");
      setPendingApply(null);
      return;
    }
    const events = await fetchMatchEvents(state.matchId);
    const target = correctionTarget(
      pendingApply.kind,
      events,
      v.subjectEventId,
      v.subjectPlayerId,
    );
    if (!target) {
      setApplyWarning(NOT_FOUND_MSG);
      setPendingApply(null);
      return;
    }
    if (pendingApply.kind === "downgrade" || pendingApply.kind === "upgrade") {
      // The card changes in place: the foul's time stays.
      const added = target.data?.added;
      await sendRaw("event.update", {
        slug,
        eventId: target.id,
        minute: target.minute,
        second: target.second,
        playerId: target.playerId,
        type: pendingApply.kind === "downgrade" ? "yellow_card" : "red_card",
        added: typeof added === "number" ? added : 0,
      });
    } else {
      await sendRaw("event.delete", { slug, eventId: target.id });
    }
    setPendingApply(null);
  };

  const eventText = (e: CatalogEvent) =>
    `${formatEventMinute(e.minute, e.second, e.data?.added)}ʼ · ${eventLabel(e.type)} · ${md.teamShort(e.teamId)} · ${md.playerName(e.playerId)}`;

  if (!v.visible) {
    return (
      <div className="flex flex-col gap-2">
        <div className="grid grid-cols-2 gap-1.5">
          {SUBJECTS.map((s) => (
            <Button key={s.id} variant="secondary" size="sm" onClick={() => selectSubject(s.id)}>
              {s.label}
            </Button>
          ))}
        </div>
        {pendingSubject && (
          <div className="flex flex-col gap-1.5 rounded-md border border-divider bg-surface-muted/40 p-2 text-[12px]">
            <span className="text-ink-soft">
              {SUBJECTS.find((s) => s.id === pendingSubject)?.label} — tekshiriladigan voqea
            </span>
            {pickable.length > 0 ? (
              <select
                value={pickedId}
                onChange={(e) => setSubjectEventId(e.target.value)}
                className={SELECT}
              >
                {pickable.map((e) => (
                  <option key={e.id} value={e.id}>
                    {eventText(e)}
                  </option>
                ))}
              </select>
            ) : (
              <span className="text-ink-faint">
                Jurnalda mos voqea yo'q — tekshiruvni voqeasiz boshlash mumkin.
              </span>
            )}
            <div className="grid grid-cols-2 gap-1.5">
              <Button variant="live" size="sm" onClick={startPending}>
                Boshlash
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setPendingSubject(null)}>
                Bekor
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  const subjectLabel = SUBJECTS.find((s) => s.id === v.subject)?.label ?? "";
  const decisions = (v.subject && DECISIONS[v.subject]) || [];

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2 text-[12px]">
        <span className="font-semibold text-ink">
          {subjectLabel}
          <span className="ml-1.5 text-ink-muted">
            {v.phase === "decided" ? "— qaror efirda" : "— tekshirilmoqda"}
          </span>
        </span>
        <Button variant="ghost" size="xs" onClick={hide}>
          Yashirish
        </Button>
      </div>
      {v.phase === "checking" && (
        <div className="grid grid-cols-2 gap-1.5">
          {decisions.map((d) => (
            <Button key={d.d} variant={d.variant} size="sm" onClick={() => decide(d.d)}>
              {d.label}
            </Button>
          ))}
        </div>
      )}
      {pendingApply && (
        <div className="rounded-md border border-live/50 bg-live-soft/40 p-2 text-[12px]">
          <div className="mb-1.5 text-ink">
            {pendingApply.kind === "rescind" && "⚠ Kartani bekor qilasizmi?"}
            {pendingApply.kind === "downgrade" && "⚠ Qizilni sariqqa tushirasizmi?"}
            {pendingApply.kind === "upgrade" && "⚠ Sariqni qizilga almashtirasizmi?"}
            {pendingApply.kind === "goal_cancel" && "⚠ Golni bekor qilasizmi?"}
            {pendingApply.kind === "yellow_cancel" && "⚠ Sariqni bekor qilasizmi?"}
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <Button variant="live" size="sm" onClick={() => void applyCorrection()}>
              Ha, qo'lla
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setPendingApply(null)}>
              Yo'q
            </Button>
          </div>
        </div>
      )}
      {applyWarning && (
        <div className="rounded-md border border-warning/50 bg-warning/10 p-2 text-[12px] text-warning">
          {applyWarning}
        </div>
      )}
    </div>
  );
}
